import { useCallback, useEffect, useState } from 'react';
import {
  fetchFormWorkflow,
  normalizeFormWorkflow,
  saveFormWorkflow,
} from '../../../lib/applicationFormWorkflow';
import { useFormsSettingsRealtime } from '../../../hooks/useFormsSettingsRealtime';
import type { FormWorkflowConfig } from '../../../types/applicationForm';
import { AfSettingsSection } from './AfSettingsSection';

const FORM_SLUG = 'account-creation';

export function ApplicationFormGeneralPanel() {
  const [workflow, setWorkflow] = useState<FormWorkflowConfig>(normalizeFormWorkflow(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setWorkflow(await fetchFormWorkflow(FORM_SLUG));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load general settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSettingsRealtime({
    tables: ['system_config'],
    onChange: () => {
      void load();
    },
  });

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await saveFormWorkflow(FORM_SLUG, workflow);
      setWorkflow(saved);
      setMessage('General settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save general settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <AfSettingsSection
      title="General"
      description="Account Creation submission channels."
    >
      {error ? <p className="alert alert-error">{error}</p> : null}
      {message ? <p className="alert alert-success">{message}</p> : null}

      {loading ? (
        <p className="page-lead">Loading…</p>
      ) : (
        <form className="card settings-panel af-workflow-layout" onSubmit={(e) => void handleSave(e)}>
          <section className="af-workflow-card">
            <h3 className="af-workflow-card__title">Submission</h3>
            <div className="af-workflow-toggles">
              <label className="af-workflow-toggle">
                <input
                  type="checkbox"
                  checked={workflow.submission.public}
                  onChange={(e) =>
                    setWorkflow((prev) => ({
                      ...prev,
                      submission: { ...prev.submission, public: e.target.checked },
                    }))
                  }
                />
                <span>Allow public customer link submissions</span>
              </label>
              <label className="af-workflow-toggle">
                <input
                  type="checkbox"
                  checked={workflow.submission.internal}
                  onChange={(e) =>
                    setWorkflow((prev) => ({
                      ...prev,
                      submission: { ...prev.submission, internal: e.target.checked },
                    }))
                  }
                />
                <span>Allow internal portal submissions</span>
              </label>
            </div>
          </section>

          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      )}
    </AfSettingsSection>
  );
}
